import { displayDateUS } from "../../utils/dateUtils";
import { HHA_EXAM_ITEMS } from "../../pages/Application/chhaPostTestConstants";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

/**
 * Flat answers: `{ "1": "A", ... }` or nested under `tests[0].answers`.
 * @param {Record<string, unknown>} record
 */
function normalizeFlatAnswers(record) {
  if (record.answers && typeof record.answers === "object") {
    return /** @type {Record<string, unknown>} */ (record.answers);
  }
  const tests = record.tests;
  const t0 = Array.isArray(tests) ? tests[0] : null;
  const nested = t0 && typeof t0 === "object" ? t0.answers : null;
  if (nested && typeof nested === "object") {
    const out = {};
    for (const [k, v] of Object.entries(nested)) {
      if (v && typeof v === "object" && "answer" in v) {
        out[k] = /** @type {{ answer?: unknown }} */ (v).answer;
      } else {
        out[k] = v;
      }
    }
    return out;
  }
  return {};
}

function scoringFromRecord(record) {
  if (record.scoringResult && typeof record.scoringResult === "object") {
    return record.scoringResult;
  }
  const tests = record.tests;
  const t0 = Array.isArray(tests) ? tests[0] : null;
  if (t0 && typeof t0 === "object" && t0.scoringResult) {
    return t0.scoringResult;
  }
  return null;
}

/**
 * Matches ChhaPostTest.jsx fetch (`result.test`, `items[0]`, or plain record).
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseChhaCompetencyTestFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const resultData = response.result || response;
  if (resultData.test && typeof resultData.test === "object") {
    return resultData.test;
  }
  if (Array.isArray(resultData) && resultData.length > 0) {
    return resultData[0];
  }
  if (resultData.items && Array.isArray(resultData.items)) {
    const uid = parseInt(String(targetUserId), 10);
    if (!Number.isNaN(uid)) {
      const found = resultData.items.find((item) => item.userId === uid);
      if (found) return found;
    }
    if (resultData.items.length > 0) return resultData.items[0];
  }
  if (
    resultData &&
    typeof resultData === "object" &&
    !Array.isArray(resultData) &&
    (resultData.id != null ||
      resultData.applicantName != null ||
      resultData.answers != null)
  ) {
    return resultData;
  }
  return null;
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/**
 * @param {Record<string, unknown>} answers
 * @param {{ id: string; text: string; type?: string; options?: { label: string; text: string }[] }} q
 */
function questionBlockHtml(answers, q) {
  const selRaw = answers[q.id];
  const selected =
    selRaw == null || selRaw === ""
      ? ""
      : String(selRaw).trim().toUpperCase();

  const opts = (q.options || [])
    .map((opt) => {
      const isSel = selected === String(opt.label).trim().toUpperCase();
      const rowClass = isSel ? "opt opt-sel" : "opt";
      return `<div class="${rowClass}"><span class="opt-lbl">${escapeHtml(opt.label)}.</span> ${escapeHtml(opt.text)}</div>`;
    })
    .join("");

  return `<section class="q-card">
    <p class="q-num">${escapeHtml(q.id)}.</p>
    <p class="q-text">${escapeHtml(q.text)}</p>
    <div class="q-opts">${opts}</div>
  </section>`;
}

/**
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildChhaCompetencyTestPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const applicantName = escapeHtml(
    String(record.applicantName || record.name || "").trim(),
  );
  const testDate = escapeHtml(formatDate(record.testDate));

  const flatAnswers = normalizeFlatAnswers(record);
  const questionsHtml = HHA_EXAM_ITEMS.map((q) =>
    questionBlockHtml(flatAnswers, q),
  ).join("");

  const score = scoringFromRecord(record);
  let scoreHtml = "";
  if (score && typeof score === "object") {
    const s = /** @type {Record<string, unknown>} */ (score);
    const pct = s.percentage != null ? escapeHtml(String(s.percentage)) : "—";
    const scr = s.score != null ? escapeHtml(String(s.score)) : "—";
    const tot =
      s.totalQuestions != null ? escapeHtml(String(s.totalQuestions)) : "—";
    const corr =
      s.correctAnswers != null ? escapeHtml(String(s.correctAnswers)) : "—";
    const incorr =
      s.incorrectAnswers != null ? escapeHtml(String(s.incorrectAnswers)) : "—";
    scoreHtml = `<div class="score-box">
      <h3 class="score-h">Scoring results</h3>
      <div class="score-grid">
        <div><span class="sl">Score</span><span class="sv">${scr} / ${tot}</span></div>
        <div><span class="sl">Correct</span><span class="sv sv-ok">${corr}</span></div>
        <div><span class="sl">Incorrect</span><span class="sv sv-bad">${incorr}</span></div>
        <div><span class="sl">Percentage</span><span class="sv">${pct}%</span></div>
      </div>
    </div>`;
  }

  const es = record.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    record.applicantSignature ||
    record.empSignature ||
    nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      record.applicantSignedAt ||
        record.applicantSignatureDate ||
        record.empSignedAt,
    ),
  );
  const applicantSignedBy = escapeHtml(
    String(record.applicantSignedBy || "").trim(),
  );

  const reviewedBySignedBy = escapeHtml(
    String(record.reviewedBySignedBy || "").trim(),
  );
  const reviewedBySig =
    record.reviewedBySignature || record.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      record.reviewedBySignedAt ||
        record.adminSignatureDate ||
        record.reviewedBySignatureDate,
    ),
  );

  const displayName = applicantName || applicantSignedBy || "—";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>CHHA competency test — ${applicantName || "Clinician"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 10px;
      line-height: 1.45;
      color: #111827;
      margin: 0;
      padding: 10px;
    }
    .sheet { max-width: 880px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body { padding: 26px 16px 36px; background: transparent; min-height: 100vh; }
      .sheet {
        background: #fff;
        padding: 28px 30px 34px;
        border: 1px solid #e2e8f0;
        box-shadow: 0 1px 2px rgba(15,23,42,0.05), 0 24px 48px -16px rgba(15,23,42,0.2);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet { padding: 0 !important; border: none !important; box-shadow: none !important; }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 14px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 12px;
    }
    .logo { height: 50px; width: auto; max-width: 190px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 14px; font-weight: 700; margin: 0 0 3px 0; }
    .title-sub { font-size: 10px; font-weight: 500; color: #2563eb; margin: 0; }
    .meta {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-bottom: 12px;
    }
    .fl { font-size: 8px; font-weight: 600; color: #6b7280; text-transform: uppercase; margin-bottom: 3px; }
    .fv {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 6px 8px;
      min-height: 30px;
      font-size: 10px;
    }
    .instr {
      background: #eff6ff;
      border-left: 4px solid #2563eb;
      padding: 10px 12px;
      margin-bottom: 14px;
      font-size: 10px;
      color: #1e3a8a;
      font-style: italic;
    }
    .q-card {
      margin-bottom: 12px;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      page-break-inside: avoid;
      background: #fafafa;
    }
    .q-num { font-weight: 700; color: #2563eb; margin: 0 0 6px 0; }
    .q-text { margin: 0 0 8px 0; color: #1e293b; font-weight: 600; text-align: justify; }
    .q-opts { display: flex; flex-direction: column; gap: 4px; }
    .opt {
      padding: 5px 8px;
      border-radius: 4px;
      font-size: 9.5px;
      text-align: justify;
      color: #475569;
    }
    .opt-sel {
      background: #dbeafe;
      border: 1px solid #93c5fd;
      font-weight: 600;
      color: #0f172a;
    }
    .opt-lbl { font-weight: 700; color: #1d4ed8; margin-right: 4px; }
    .score-box {
      margin: 14px 0;
      padding: 12px;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
      background: #f8fafc;
    }
    .score-h { font-size: 11px; font-weight: 700; margin: 0 0 8px 0; color: #1e3a8a; }
    .score-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px 12px;
      font-size: 10px;
    }
    .sl { color: #64748b; display: block; font-size: 8px; text-transform: uppercase; }
    .sv { font-weight: 700; color: #0f172a; }
    .sv-ok { color: #15803d; }
    .sv-bad { color: #b91c1c; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-top: 16px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px;
      background: #fafafa;
    }
    .sig-card h3 { font-size: 11px; font-weight: 700; margin: 0 0 8px 0; }
    .sig-label { font-size: 8px; font-weight: 600; color: #6b7280; margin: 6px 0 3px 0; }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 26px;
      font-size: 10px;
      padding: 3px 0;
    }
    .sig-img { max-height: 40px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 16px;
      padding-top: 8px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 8px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">CHHA competency test</p>
        <p class="title-sub">Certified Home Health Aide · ${company}</p>
      </div>
    </div>

    <div class="meta">
      <div><div class="fl">Clinician / orientee name</div><div class="fv">${applicantName || "—"}</div></div>
      <div><div class="fl">Date</div><div class="fv">${testDate || "—"}</div></div>
    </div>

    <p class="instr">Please select the best answer for the following questions to demonstrate your competency as a Certified Home Health Aide.</p>

    ${questionsHtml}

    ${scoreHtml}

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee / clinician</h3>
        <div class="sig-label">Name</div>
        <div class="fv" style="margin-bottom:6px;">${displayName}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="fv" style="margin-bottom:6px;">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
